import createMentionPlugin, { MentionSuggestions, MentionSuggestionsProps } from "draft-js-mention-plugin";
import * as React from "react";
import { useRef } from "react";
import { Keys } from "~/common/enums";
import * as styles from "../AdvancedEditor.less";
import { MentionPlugin } from "../types/types";
import positionSuggestions from "../utils/positionSuggestions";

type MentionsProps = { isFocused?: boolean };
type Result = MentionPlugin & {
    Mentions: React.ComponentType<MentionsProps>;
};

// wrapper that stops propagation for esc keydown event if mentions popup is opened
// to prevent existing from exit mode in this case.
export const EpMentionSuggestions: React.FC<MentionSuggestionsProps> = ({ callbacks, onClose, onOpen, ...props }) => {
    const isOpened = React.useRef(false);
    const wrappedCallbacks = React.useRef({
        onChange: undefined,
        handleReturn: undefined,
        keyBindingFn: undefined,
    } as typeof callbacks);

    React.useEffect(() => {
        callbacks.onChange = s => wrappedCallbacks.current.onChange(s);
        callbacks.handleReturn = () => wrappedCallbacks.current.handleReturn?.();
        callbacks.keyBindingFn = e => {
            if (isOpened.current && e.key === Keys.Escape) {
                e.stopPropagation();
            }
            return wrappedCallbacks.current.keyBindingFn?.(e);
        };

        return () => {
            callbacks.onChange = undefined;
            callbacks.keyBindingFn = undefined;
            callbacks.handleReturn = undefined;
        };
    }, []);

    const onCloseCallback = () => {
        isOpened.current = false;
        onClose?.();
    };

    const onOpenCallback = () => {
        isOpened.current = true;
        onOpen?.();
    };

    return (
        <MentionSuggestions
            {...props}
            callbacks={wrappedCallbacks.current}
            onClose={onCloseCallback}
            onOpen={onOpenCallback}
        />
    );
};

type UseMentionPluginProps<T, F> = {
    mentionListProps: T;
    MentionList: React.FC<T>;
    mentionComponent: (props: F) => JSX.Element;
};

export const useMentionPlugin = <T, F>(props: UseMentionPluginProps<T, F>) => {
    const { MentionList } = props;
    const ref = useRef<Result>();

    if (!ref.current) {
        const plugin = createMentionPlugin({
            theme: styles,
            mentionSuggestionsComponent: EpMentionSuggestions,
            positionSuggestions,
            mentionPrefix: "@",
            mentionComponent: props.mentionComponent,
        });

        const Mentions = (mentionsProps: MentionsProps) => (
            <MentionList plugin={plugin} {...props.mentionListProps} {...mentionsProps} />
        );

        ref.current = {
            plugin,
            Mentions,
        };
    }

    return ref.current;
};
